import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectMonthEndReview,
  selectMonthEndStatus,
} from "../../features/billing/billing-slice";
import {
  confirmAllMonthEnd,
  fetchMonthEndReview,
  generateMonthEndDrafts,
} from "../../features/billing/slice-extensions/billing-slice-extension";
import { confirmPayment } from "../../features/payments/slice-extensions/payments-slice-extension";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { MonthEndDraft } from "../../features/billing/models/MonthEndReview";
import { formatMoney } from "../../features/billing/utils/money";
import { contractTypeLabel } from "../../features/payments/utils/paymentLabels";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import { fetchMembers } from "../../features/members/slice-extensions/members-slice-extension";
import MonthlyStatementSection from "./MonthlyStatementSection";
import { ROUTES } from "../../routes";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

/** The previous calendar month — the period the monthly draft job bills. */
const defaultPeriod = (): { year: number; month: number } => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
};

/**
 * The month-end reveal: the month's subscription Payment drafts, each with its
 * closed-period / pro-rating explanation, the per-visit account drafts with their
 * lines (count × payment type · subtotal), and the one-click confirm-all.
 */
const BillingMonthEndTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const review = useAppSelector(selectMonthEndReview);
  const status = useAppSelector(selectMonthEndStatus);
  const memberName = useMemberNameLookup();

  const [period, setPeriod] = useState(defaultPeriod);
  const [busy, setBusy] = useState(false);

  const monthLabel = (year: number, month: number): string =>
    new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" }).format(
      new Date(year, month - 1, 1)
    );

  const reload = () => dispatch(fetchMonthEndReview(period));

  useEffect(() => {
    dispatch(fetchMonthEndReview(period));
    dispatch(fetchMembers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, period]);

  const shiftMonth = (delta: number) => {
    const d = new Date(period.year, period.month - 1 + delta, 1);
    setPeriod({ year: d.getFullYear(), month: d.getMonth() + 1 });
  };

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const confirmAll = () => run(() => dispatch(confirmAllMonthEnd(period)).unwrap());
  const confirmOne = (draft: MonthEndDraft) =>
    run(() => dispatch(confirmPayment({ id: draft.paymentId, request: {} })).unwrap());
  const generate = () => run(() => dispatch(generateMonthEndDrafts(period)).unwrap());

  const drafts = review?.drafts ?? [];
  const attentionCount = drafts.filter((d) => d.needsAttention).length;
  const label = monthLabel(period.year, period.month);

  const readyDetails = attentionCount > 0
    ? `${t("billing.monthEnd.draftCount", { count: drafts.length })}, ${t("billing.monthEnd.attentionCount", { count: attentionCount })}`
    : t("billing.monthEnd.draftCount", { count: drafts.length });

  return (
    <div className="mt-4 space-y-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between gap-2">
        <button type="button" className="btn btn-secondary px-3 py-2" onClick={() => shiftMonth(-1)} aria-label={t("billing.monthEnd.previousMonth")} disabled={busy}>
          <ChevronLeft size={18} />
        </button>
        <p className="font-semibold text-grey-10 dark:text-grey-90">{label}</p>
        <button type="button" className="btn btn-secondary px-3 py-2" onClick={() => shiftMonth(1)} aria-label={t("billing.monthEnd.nextMonth")} disabled={busy}>
          <ChevronRight size={18} />
        </button>
      </div>

      {status === LoadingStatus.Loading && !review ? (
        <TableRowsSkeleton rows={4} />
      ) : drafts.length > 0 ? (
        <>
          {/* The reveal headline */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-grey-90 bg-primary-95 p-4 lg:p-5 dark:border-primary-20 dark:bg-primary-20">
            <div className="flex items-start gap-3">
              <Sparkles size={22} className="mt-0.5 shrink-0 text-primary-40 dark:text-primary-90" />
              <div>
                <p className="font-semibold text-grey-10 dark:text-grey-90">
                  {t("billing.monthEnd.ready", { month: label, details: readyDetails })}
                </p>
                <p className="mt-0.5 text-sm text-grey-30 dark:text-grey-70">
                  {t("billing.monthEnd.reviewHint")}
                </p>
              </div>
            </div>
            <button type="button" className="btn btn-primary" onClick={confirmAll} disabled={busy}>
              <CheckCheck size={18} />
              <span>{t("billing.monthEnd.confirmAll")}</span>
            </button>
          </div>

          {/* Per-member drafts */}
          <div className="space-y-2">
            {drafts.map((d) => (
              <div key={d.paymentId} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-grey-90 px-3 py-2.5 dark:border-primary-20">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-grey-10 dark:text-grey-90">
                    <Link to={ROUTES.MEMBER_BILLING.replace(":id", d.memberId)} className="hover:underline">
                      {memberName(d.memberId)}
                    </Link>
                    <span className="ml-2 font-normal text-grey-30 dark:text-grey-70">
                      {contractTypeLabel(d.contractType, t)}
                    </span>
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-grey-30 dark:text-grey-70">
                    <span className="font-semibold text-grey-10 dark:text-grey-90">
                      {formatMoney(d.amountMinorUnits, d.amountCurrency, getDisplayLocale())}
                    </span>
                    {d.isAdjusted && (
                      <span>{t("billing.monthEnd.adjustedFrom", { amount: formatMoney(d.fullFeeMinorUnits, d.fullFeeCurrency, getDisplayLocale()) })}</span>
                    )}
                    {d.needsAttention && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-95 px-2 py-0.5 font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
                        <AlertTriangle size={11} />
                        {t("billing.common.needsAttention")}
                      </span>
                    )}
                  </p>
                  {d.lines.length > 0 ? (
                    <ul className="mt-1 text-xs text-grey-30 dark:text-grey-70">
                      {d.lines.map((line) => (
                        <li key={`${line.paymentTypeId}-${line.unitPriceMinorUnits}`}>
                          {t("billing.monthEnd.linesSummary", { count: line.count, name: line.paymentTypeName })}
                          {" · "}
                          {formatMoney(line.subtotalMinorUnits, line.currency, getDisplayLocale())}
                        </li>
                      ))}
                    </ul>
                  ) : d.notes && (
                    <p className="mt-1 text-xs text-grey-30 dark:text-grey-70">{d.notes}</p>
                  )}
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => confirmOne(d)} disabled={busy}>
                  <Check size={16} />
                  <span>{t("billing.actions.confirm")}</span>
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-grey-90 p-6 text-center dark:border-primary-20">
          {review && review.confirmedCount > 0 ? (
            <p className="inline-flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
              <Check size={18} />
              {t("billing.monthEnd.allConfirmed", { count: review.confirmedCount, month: label })}
            </p>
          ) : (
            <>
              <p className="text-sm text-grey-30 dark:text-grey-70">
                {t("billing.monthEnd.noDrafts", { month: label })}
              </p>
              <button type="button" className="btn btn-primary mt-4" onClick={generate} disabled={busy}>
                <FilePlus2 size={18} />
                <span>{t("billing.monthEnd.generateDrafts")}</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* The month read back: money, delivery, per-member totals. */}
      <MonthlyStatementSection year={period.year} month={period.month} />
    </div>
  );
};

export default BillingMonthEndTab;
